<template>
    <div
        class="dropdown"
        :class="{ 'is-active' : showDropdown }"
        ref="dropdown"
    >
        <div class="selected-option-con">
            <div
                v-show="!active"
                class="option-display-con"
                @click="toggle"
            >
                <slot name="selected-option">
                    <div class="input">
                        {{selectedOptionPlaceholder}}
                    </div>
                </slot>
            </div>
            <input
                ref="input"
                class="input"
                v-model="search"
                :placeholder="localInputPlaceholder"
                @change="$emit('{ event : $event, search : search }', search)"
                @keydown="$emit('keydown', { event : $event, search : search })"
                @keyup="$emit('keyup', { event : $event, search : search })"
                @keypress="$emit('keypress', { event : $event, search : search })"
                @focus="$emit('focus', { event : $event, search : search })"
                @focusin="$emit('focusin', { event : $event, search : search })"
                @focusout="$emit('focusout', { event : $event, search : search })"
            />
            <span
                v-if="showIcon"
                class="icon is-small toggle-icon"
            >
                <i class="fa fa-angle-down" aria-hidden="true"></i>
            </span>
        </div>
        <div class="dropdown-menu" id="dropdown-menu" role="menu">
            <div class="dropdown-content">
                <slot  name="header">
                </slot>
                <slot name="options">
                    <div v-if="!isGrouped">
                        <a
                            v-for="(opt, index) in filteredOptions"
                            :key="index"
                            class="dropdown-item"
                            :class="{ 'is-active' : opt[id] === value }"
                            @click="selected(opt, opt)"
                        >
                            <slot name="option" :option="opt" :option-index="index">
                                {{opt[label]}}
                            </slot>
                        </a>
                    </div>
                    <div v-else>
                        <div
                            v-for="(group, groupIndex) in filteredOptions"
                            :key="groupIndex"
                        >
                            <div class="group">
                                <slot name="group" :group="group">
                                    <h5 class="heading">{{group[groupLabel]}}</h5>
                                </slot>
                            </div>
                            <a
                                v-for="(opt, optionIndex) in group[groupOptionsKey]"
                                :key="optionIndex"
                                :class="{ 'is-active' : value && value.indexOf(opt[id]) !== -1 }"
                                @click="selected($event, opt)"
                                class="dropdown-item"
                            >
                                <slot name="option" :option="opt" :group-index="groupIndex" :option-index="optionIndex">
                                    {{opt[label]}}
                                </slot>

                            </a>
                        </div>
                    </div>
                </slot>
                <slot
                    v-if="!filteredOptions.length"
                    name="no-results"
                >
                </slot>
                <slot  name="footer">
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name : "DropdownInput",
    props : {
        value : false,
        options : {
            type : Array,
            required : false,
        },
        default : () => {
            return [];
        },
        id : {
            type : String,
            required : false,
            default : 'id',
        },
        label : {
            type : String,
            required : false,
            default : 'name'
        },
        placeholder : {
            type : String,
            required : false,
        },
        inputPlaceholder : {
            type : String,
            required : false,
        },
        hideWhenNoResults : {
            type : Boolean,
            required : false,
            default : false,
        },
        closeOnSelect : {
            type : Boolean,
            default : true,
        },
        isGrouped : {
            type : Boolean,
            required : false,
            default : false,
        },
        groupLabel : {
            type : String,
            required : false,
            default : 'name',
        },
        groupOptionsKey : {
            type : String,
            required : false,
            default : 'options',
        },
        showEmptyGroupsOnSearch : {
            type : Boolean,
            required : false,
            default : false,
        },
        showIcon : {
            type : Boolean,
            required : false,
            default : true,
        },
        disabled : {
            type : Boolean,
            required : false,
            default : false,
        },
    },
    data () {
        return {
            locale : 'en',
            active : false,
            search : '',
        };
    },
    computed : {
        showDropdown () {
            return this.active && !(this.hideWhenNoResults && !this.filteredOptions.length);
        },
        selectedOptionPlaceholder () {
            if(!this.value){
                return this.placeholder;
            }
            const label = this.selectedOptionLabel;
            return label ? label : this.placeholder;
        },
        selectedOption () {
            const find = (options) => {
                for (let i = 0; i < options.length; i++) {
                    if (options[i][this.id] === this.value) {
                        return options[i];
                    }
                }
                return null;
            };
            if(!this.isGrouped) {
                return find(this.options);
            }
            for(let i=0; i < this.options.length; i++) {
                const opt = find(this.options[i]);
                if(opt){
                    return opt;
                }
            }
            return null;
        },
        selectedOptionLabel () {
            const opt = this.selectedOption;
            if(opt){
                return opt[this.label];
            }
            return null;
        },
        searchExpression () {
            return new RegExp(this.search, 'i');
        },
        filteredOptions () {
            if(!this.search){
                return this.options;
            }
            const filter = (options) => {
                return options.filter(opt => {
                    return opt[this.label].match(this.searchExpression);
                })
            };
            if(!this.isGrouped) {
                return filter(this.options);
            }
            const groups = this.options.map(group => {
                return {
                    ...group,
                    [this.groupOptionsKey] : filter(group[this.groupOptionsKey]),
                };
            });
            if(this.showEmptyGroupsOnSearch) {
                return groups;
            }
            return groups.filter(group => {
                return group[this.groupOptionsKey].length;
            });
        },
        localInputPlaceholder () {
            if(this.inputPlaceholder){
                return this.inputPlaceholder;
            }
            return this.selectedOptionLabel;
        },
    },
    methods : {
        toggle () {
            if (this.disabled) {
                return;
            }
            if(!this.active){
                if(this.$refs.input) {
                    this.$refs.input.focus();
                }
                this.active = true;
                window.addEventListener('click', this.close, { passive : true });
                this.$emit('open');
            } else {
                this.active = false;
                this.search = '';
                this.$emit('close');
                this.$emit('search', '');
                window.removeEventListener('click', this.close);
            }
        },
        close (event) {
            if(this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)){
                this.$emit('close');
                this.active = false;
                window.removeEventListener('click', this.close);
            }
        },
        selected ($event, opt) {
            this.$emit('selected', { option : opt, event : $event });
            if(this.closeOnSelect) {
                this.toggle();
            }
        }
    },
}
</script>

<style lang="scss" scoped>
    @import '../assets/css/_theme';
    .selected-option-con {
        position:relative;
        .toggle-icon {
            position:absolute;
            right:10px;
            top:.63em;
            z-index:11;
        }
    }
    .dropdown-menu {
        padding-top:0;
        width:100%;
        max-height:80vh;
        overflow-y:auto;
        border:1px solid $grey-lighter;
    }
    .selected-option-con,
    .option-display-con {
        width:100%;
        max-width:100%;
    }

    .option-display-con {
        z-index:10;
        position:absolute;
        top:0;
        left:0;
        width:100%;
        min-height:100%;
    }

    .group {
        text-align:left;
        padding:4px 6px;
        background-color:$white-ter;
        h5 {
            font-size:11px;
            font-weight:bold;
            text-transform:uppercase;
            margin:0;
        }
    }

    .dropdown-item {
        text-align:left;
    }

</style>
